import { useTranslations } from '@vocab/react';
import { useToast } from 'braid-design-system';
import { observer } from 'mobx-react';

import { Modal } from 'src/components/common';
import { OVERLAY_MODAL } from 'src/constants/modals';
import {
  EVENT_NAME_MANAGE_NOTIFICATIONS_SELF_CANCEL_PRESSED,
  EVENT_NAME_MANAGE_NOTIFICATIONS_JOB_EXPIRED_RETURN_PRESSED,
} from 'src/constants/pageTrackingData';
import { trackEvent } from 'src/modules/events';
import type { IJob } from 'src/stores/JobStore/Job';
import type { IModalStore } from 'src/stores/UiStore/ModalStore';
import useQueryParamsStore from 'src/utils/hooks/useQueryParamsStore';

import translations from './.vocab';
import NotificationPreference from './NotificationPreference';

import * as styles from './NotificationPreferenceModal.css';

interface Props {
  modalStore?: IModalStore;
  job: IJob;
}

export const NotificationPreferenceModal = ({ modalStore, job }: Props) => {
  const { t } = useTranslations(translations);
  const [, setQueryParams] = useQueryParamsStore();
  const seekAdvertiserId = job?.seekAdvertiserId || '';
  const modalName = OVERLAY_MODAL.USER_PREFERENCES;

  const showToast = useToast();

  const removeModal = () => {
    setQueryParams.clearOverlayModal();
  };

  const onCancel = () => {
    trackEvent(EVENT_NAME_MANAGE_NOTIFICATIONS_SELF_CANCEL_PRESSED, {});
    removeModal();
  };

  const onReturnToJob = () => {
    trackEvent(EVENT_NAME_MANAGE_NOTIFICATIONS_JOB_EXPIRED_RETURN_PRESSED, {});
    removeModal();
  };

  const onUpdateSuccess = () => {
    removeModal();
    showToast({
      tone: 'positive',
      message: t('Email notifications updated'),
      closeLabel: t('Close'),
    });
  };

  return (
    <Modal
      modalStore={modalStore!}
      name={modalName}
      onCloseModal={onCancel}
      contentClassName={styles.notificationModalContent}
    >
      {() => (
        <NotificationPreference
          seekAdvertiserId={seekAdvertiserId}
          onCancel={onCancel}
          onSubmitSuccess={onUpdateSuccess}
          onReturnToJob={onReturnToJob}
          job={job}
        />
      )}
    </Modal>
  );
};
export default observer(NotificationPreferenceModal);
